import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FitRow {
  id: string;
  years: string | null;
  make: string | null;
  model: string | null;
  tab_type: string | null;
  tab_width: string | null;
  tab_height: string | null;
  weight_lbs: string | null;
  install_rating: string | null;
  wiring_code: string | null;
  note_codes: number[] | null;
}

/**
 * Blue Ox baseplate fit guide. Renders only when the part number has current
 * fit-guide rows, so it is a no-op on every non-baseplate product.
 */
export default function BlueOxBaseplateFitment({ partNumber }: { partNumber?: string | null }) {
  const [rows, setRows] = useState<FitRow[]>([]);
  const [notes, setNotes] = useState<{ code: number; note_text: string }[]>([]);

  useEffect(() => {
    const pn = (partNumber || "").trim();
    if (!pn) {
      setRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("blueox_baseplate_fitment")
        .select("id, years, make, model, tab_type, tab_width, tab_height, weight_lbs, install_rating, wiring_code, note_codes")
        .eq("part_number", pn)
        .eq("is_current", true)
        .order("make", { ascending: true })
        .order("model", { ascending: true });
      if (cancelled) return;
      const list = (data || []) as FitRow[];
      setRows(list);
      const codes = Array.from(new Set(list.flatMap((r) => r.note_codes || [])));
      if (codes.length > 0) {
        const { data: noteData } = await supabase
          .from("blueox_baseplate_notes")
          .select("code, note_text")
          .in("code", codes)
          .order("code", { ascending: true });
        if (!cancelled) setNotes(noteData || []);
      } else {
        setNotes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partNumber]);

  if (rows.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold mb-3">Baseplate Fit Guide</h2>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Years</th>
              <th className="px-3 py-2 font-semibold">Make</th>
              <th className="px-3 py-2 font-semibold">Model</th>
              <th className="px-3 py-2 font-semibold">Tab Type</th>
              <th className="px-3 py-2 font-semibold">Tab W</th>
              <th className="px-3 py-2 font-semibold">Tab H</th>
              <th className="px-3 py-2 font-semibold">Weight</th>
              <th className="px-3 py-2 font-semibold">Install</th>
              <th className="px-3 py-2 font-semibold">Wiring</th>
              <th className="px-3 py-2 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} className="odd:bg-card even:bg-muted/20">
                <td className="px-3 py-2 whitespace-nowrap">{r.years || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.make || "—"}</td>
                <td className="px-3 py-2">{r.model || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.tab_type || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.tab_width || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.tab_height || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.weight_lbs || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.install_rating || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.wiring_code || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {(r.note_codes || []).length > 0 ? (r.note_codes || []).join(", ") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {notes.length > 0 && (
        <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
          {notes.map((n) => (
            <div key={n.code} className="flex gap-2">
              <dt className="font-semibold text-foreground">{n.code}.</dt>
              <dd>{n.note_text}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Always confirm fitment for your exact vehicle before installing.
      </p>
    </section>
  );
}
